/** @sfdc-extension-file SNK */
'use client';

import { Suspense, useMemo, type ComponentProps } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { Region, type ComponentType } from '@/components/region';
import { CATEGORY_SUBMENU_SLOT_CONTENT_REGION_ID } from '@/extensions/snk/lib/category-submenu-slots';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';
import { cn } from '@/lib/utils';

interface CategorySubmenuSlotProps extends ComponentProps<'div'> {
    categoryId?: string;

    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('categorySubmenuSlot', {
    name: 'Category Submenu Slot',
    description:
        'Optional Page Designer content shown below category links in the burger menu submenu for a single category.',
})
@RegionDefinition([
    {
        id: 'content',
        name: 'Submenu content',
        description: 'Add Content Card components rendered below the category link list.',
        componentTypeInclusions: ['contentCard', 'linkCard'],
    },
])
export class CategorySubmenuSlotMetadata {
    @AttributeDefinition({
        id: 'categoryId',
        name: 'Category',
        description: 'SCAPI category ID for the submenu where this content appears.',
        type: 'category',
        required: true,
    })
    categoryId?: string;
}
/* v8 ignore stop */

export default function CategorySubmenuSlot({
    className,
    categoryId,
    regionId: _regionId,
    component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    ...props
}: CategorySubmenuSlotProps) {
    const componentWithRegionMetadata = useMemo(
        () => (component ? withComponentRegionDesignMetadata(component, CategorySubmenuSlotMetadata) : undefined),
        [component]
    );

    if (!componentWithRegionMetadata) {
        return null;
    }

    return (
        <div
            className={cn('flex w-full flex-col', className)}
            data-category-id={categoryId}
            data-testid="category-submenu-slot"
            {...props}>
            <Suspense fallback={null}>
                <Region
                    component={componentWithRegionMetadata}
                    regionId={CATEGORY_SUBMENU_SLOT_CONTENT_REGION_ID}
                    errorElement={null}
                />
            </Suspense>
        </div>
    );
}
